import dayjs from 'dayjs';
import { OpenAPI } from './api/core/OpenAPI';
import { AdminAuthToken } from './api/models/AdminAuthToken';
import { FolderResponse } from './api/models/FolderResponse';
import { FolderUnitResponseServiceResult } from './api/models/FolderUnitResponseServiceResult';
import { LoginRequest } from './api/models/LoginRequest';
import { SleeperReportingMode } from './api/models/SleeperReportingMode';
import { AuthenticationApiClient } from './api/services/AuthenticationApiClient';
import { SleeperUnitHistoryApiClient } from './api/services/SleeperUnitHistoryApiClient';
import { SleeperUnitInformationApiClient } from './api/services/SleeperUnitInformationApiClient';
import { UnitFoldersApiClient } from './api/services/UnitFoldersApiClient';

export function configureApi() {
  OpenAPI.BASE = String(process.env.REACT_APP_API_URL);
  OpenAPI.HEADERS = headerResolver();
}

const headerResolver = (): Record<string, string> => {
  const headers: Record<string, string> = {};

  if (ApiService.token) {
    headers.Authorization = `Bearer ${ApiService.token.token}`;
  } else {
    headers.Authorization = '';
  }

  return headers;
};

export class ApiService {

  public static get token() {
    return this._token;
  }

  private static _token: AdminAuthToken | null;

  public static setToken(token: AdminAuthToken | null) {
    this._token = token;
    OpenAPI.HEADERS = headerResolver();
  }

  public static hasValidToken(): boolean {
    if (this.token === null) {
      return false;
    }

    return dayjs(this.token.expiresOn).isAfter(dayjs());
  }

  //authentication
  public static async login(request: LoginRequest): Promise<void> {
    try {
      const response = await AuthenticationApiClient.postApiV1Login(request);

      if (response === null) {
        throw new Error('Empty login response');
      }
      if (response.success === false) {
        throw new Error('Login failed');
      }
      if (!response.value || !response.value.token) {
        throw new Error('Incorrect login response');
      }
      this.setToken(response.value.token);
    } catch (err) {
      console.dir(err);
      throw err;
    }
  }

  public static async sendResetPasswordRequest(emailId: string) {
    const response = await AuthenticationApiClient.postApiV1LoginResetPassword(emailId);
    if (!response) {
      throw new Error('Request failed');
    }
    return response.success || false;
  }

  public static async updatePassword(guid: string, password: string) {
    const response = await AuthenticationApiClient.postApiV1LoginUpdatePassword(guid, { newPassword: password });
    if (!response) {
      throw new Error('Request failed');
    }
    return response.success || false;
  }

  //accounts/folders
  public static async getFolders(): Promise<FolderResponse[]> {
    const response = await UnitFoldersApiClient.getApiV1Folder();
    return response.value || [];
  }

  //sleeper units
    public static async getUnitsInFolder(folderId: number, unitName?: string, nextPageNo?: number): Promise<FolderUnitResponseServiceResult> {
        return await SleeperUnitInformationApiClient.getApiV1UnitInFolder(folderId, unitName, nextPageNo);
  }

  public static async getUnit(unitId: number) {
    const response = await SleeperUnitInformationApiClient.getApiV1Unit(unitId);
    if (!response || !response.success || !response.value) {
      throw new Error('Request failed');
    }
    return response.value;
  }

  public static async toggleGpsMode(unitId: number, reportingMode: SleeperReportingMode) {
    const response = await SleeperUnitInformationApiClient.postApiV1UnitToggleGpsMode(unitId, { reportingMode });
    return response.success;
  }

  //unit history
  public static async getLocationHistory(unitId: number) {
    const response = await SleeperUnitHistoryApiClient.getApiV1UnitLocationHistory(unitId);
    if (!response || !response.success) {
      throw new Error('Request failed');
    }
    return response.value?.locations || [];
  }

  public static async getAlertHistory(unitId: number) {
    const response = await SleeperUnitHistoryApiClient.getApiV1UnitAlertHistory(unitId);
    if (response === null || !response.success) {
      throw new Error('Request failed');
    }
    return response.value?.alerts || [];
  }
}
